import { useState } from "react";
import useSWR from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import {
  type ReassistBillingResponse,
  type ReassistRequestDetail,
  type ReassistRequestListResponse,
} from "../types";

const BASE_URL = "real-estate-document-fetch/requests/";

/** 依頼詳細を開いたままのときに取り直す間隔（納品資料のURLは1時間で失効するため、その半分）。 */
const DETAIL_REFRESH_MS = 30 * 60 * 1000;

/** 取得結果。error があれば取得に失敗している（retry で再取得できる）。 */
export interface ReassistFetchState<T> {
  data: T | undefined;
  error: unknown;
  retry: () => void;
}

/**
 * ログインユーザー（共有が許可された会社なら同じ会社）の取得依頼の一覧を取得する。
 * @returns 一覧。取得中は data が undefined、失敗時は error を持つ
 */
export const useReassistRequests =
  (): ReassistFetchState<ReassistRequestListResponse> => {
    const { apiClient } = useApiClient();
    const { data, error, mutate } = useSWR<
      ReassistRequestListResponse,
      unknown
    >(
      BASE_URL,
      async () =>
        (await apiClient.get<ReassistRequestListResponse>(BASE_URL)).data,
      { revalidateOnFocus: false }
    );
    return {
      data,
      error,
      retry: () => {
        mutate().catch(() => undefined);
      },
    };
  };

/**
 * 取得依頼の月別の請求額（確定額・暫定額）を取得する。
 * @returns 月別の集計。取得中は data が undefined、失敗時は error を持つ
 */
export const useReassistBilling =
  (): ReassistFetchState<ReassistBillingResponse> => {
    const { apiClient } = useApiClient();
    const url = `${BASE_URL}billing/`;
    const { data, error, mutate } = useSWR<ReassistBillingResponse, unknown>(
      url,
      async () => (await apiClient.get<ReassistBillingResponse>(url)).data,
      { revalidateOnFocus: false }
    );
    return {
      data,
      error,
      retry: () => {
        mutate().catch(() => undefined);
      },
    };
  };

/**
 * 取得依頼の詳細（納品済みの完全版のダウンロードURLを含む）を取得する。
 * 開くたび（この hook を使う部品を表示するたび）に新しく取得し、前回の期限つきURLをキャッシュから出さない。
 * @param requestId - 依頼ID。未指定の場合は取得しない
 * @returns 詳細。取得中は data が undefined、失敗時は error を持つ
 */
export const useReassistRequestDetail = (
  requestId: string | undefined
): ReassistFetchState<ReassistRequestDetail> => {
  const { apiClient } = useApiClient();
  const url = requestId !== undefined ? `${BASE_URL}${requestId}/` : null;
  // 表示のたびに別のキーにして、SWR のキャッシュにある前回の（期限切れかもしれない）ダウンロードURLを使わない
  const [openedAt] = useState<number>(() => Date.now());
  const { data, error, mutate } = useSWR<ReassistRequestDetail, unknown>(
    url === null ? null : [url, openedAt],
    async () =>
      (await apiClient.get<ReassistRequestDetail>(url as string)).data,
    // ダウンロードURLの有効期限（1時間）が切れる前に取り直し、開いたままでも使えるようにする
    {
      revalidateOnFocus: false,
      dedupingInterval: 0,
      refreshInterval: DETAIL_REFRESH_MS,
    }
  );
  return {
    data,
    error,
    retry: () => {
      mutate().catch(() => undefined);
    },
  };
};
